import { formatForeignMap } from '../../utils/fcDocuments';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableFooter, TableHead, TableRow, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { SalesReportService } from '../../services/salesReport.service';
import { ArAgingReport as ArAgingReportData } from '../../interfaces/sales/salesReport.interface';
import { formatMoney } from '../../utils/format';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { downloadCsv } from '../../utils/csv';

const ArAgingReport: React.FC = () => {
  const toast = useToast();
  const [report, setReport] = useState<ArAgingReportData | null>(null);
  const hasFc = !!report?.rows.some(r => formatForeignMap(r.foreignOutstanding));
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const data = await SalesReportService.aging(institutionId);
      setReport(data);
    } catch (e) {
      toast.showApiError(e, 'Failed to load AR aging report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!report) return;
    const headers = ['Customer', 'Current', '1-30', '31-60', '61-90', '90+', 'Total', 'On-account credit'];
    const dataRows = report.rows.map(r => [
      r.customerName,
      r.bucketCurrent,
      r.bucket0_30,
      r.bucket31_60,
      r.bucket61_90,
      r.bucket90Plus,
      r.total,
      r.unappliedCredit ?? 0,
    ]);
    const totalsRow = [
      'TOTALS',
      report.grandBucketCurrent,
      report.grandBucket0_30,
      report.grandBucket31_60,
      report.grandBucket61_90,
      report.grandBucket90Plus,
      report.grandTotal,
      report.grandUnappliedCredit ?? 0,
    ];
    downloadCsv('ar-aging.csv', headers, [...dataRows, totalsRow]);
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.arAging.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5">AR Aging Report</Typography>
          {report && (
            <Typography variant="body2" color="text.secondary">
              As of {report.asOfDate}
            </Typography>
          )}
        </Box>
        <Button
          data-help="sales.arAging.export"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCsv}
          disabled={!report || loading}
        >
          Export CSV
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : report && (
        <TableContainer component={Paper} data-help-section="sales.arAging.table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.arAging.col.customer">Customer</TableCell>
                <TableCell align="right" data-help="sales.arAging.col.current">Current</TableCell>
                <TableCell align="right" data-help="sales.arAging.col.b30">1–30</TableCell>
                <TableCell align="right" data-help="sales.arAging.col.b60">31–60</TableCell>
                <TableCell align="right" data-help="sales.arAging.col.b90">61–90</TableCell>
                <TableCell align="right" data-help="sales.arAging.col.b90plus">90+</TableCell>
                <TableCell align="right" data-help="sales.arAging.col.total">Total</TableCell>
                <TableCell align="right" data-help="sales.arAging.col.credit">On-account credit</TableCell>
                {hasFc && <TableCell align="right" data-help="sales.arAging.col.foreign">Open in foreign currency</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {report.rows.map(row => (
                <TableRow key={row.customerId} hover>
                  <TableCell>
                    <RouterLink
                      to={`/sales/reports/statement?customerId=${row.customerId}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {row.customerName}
                    </RouterLink>
                  </TableCell>
                  <TableCell align="right">{formatMoney(row.bucketCurrent)}</TableCell>
                  <TableCell align="right">{formatMoney(row.bucket0_30)}</TableCell>
                  <TableCell align="right">{formatMoney(row.bucket31_60)}</TableCell>
                  <TableCell align="right">{formatMoney(row.bucket61_90)}</TableCell>
                  <TableCell align="right">{formatMoney(row.bucket90Plus)}</TableCell>
                  <TableCell align="right">{formatMoney(row.total)}</TableCell>
                  <TableCell align="right">{formatMoney(row.unappliedCredit ?? 0)}</TableCell>
                  {hasFc && <TableCell align="right">{formatForeignMap(row.foreignOutstanding)}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                <TableCell>TOTALS</TableCell>
                <TableCell align="right">{formatMoney(report.grandBucketCurrent)}</TableCell>
                <TableCell align="right">{formatMoney(report.grandBucket0_30)}</TableCell>
                <TableCell align="right">{formatMoney(report.grandBucket31_60)}</TableCell>
                <TableCell align="right">{formatMoney(report.grandBucket61_90)}</TableCell>
                <TableCell align="right">{formatMoney(report.grandBucket90Plus)}</TableCell>
                <TableCell align="right">{formatMoney(report.grandTotal)}</TableCell>
                <TableCell align="right">{formatMoney(report.grandUnappliedCredit ?? 0)}</TableCell>
                {hasFc && <TableCell />}
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ArAgingReport;
